import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

const Window = ({ 
  title, 
  icon, 
  children, 
  onClose, 
  onMinimize, 
  onMaximize, 
  isMaximized = false,
  width = 400,
  height = 300,
  x = 100,
  y = 100
}) => {
  const MIN_WIDTH = 320;
  const MIN_HEIGHT = 220;

  const [size, setSize] = useState({ width, height });
  const isResizingRef = useRef(false);
  const startMouseRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ width, height });

  useEffect(() => {
    setSize({ width, height });
  }, [width, height]);

  // Global mouse handlers for resizing
  const handleMouseMove = (e) => {
    if (!isResizingRef.current) return;
    const dx = e.clientX - startMouseRef.current.x;
    const dy = e.clientY - startMouseRef.current.y;
    const nextWidth = Math.max(MIN_WIDTH, startSizeRef.current.width + dx);
    const nextHeight = Math.max(MIN_HEIGHT, startSizeRef.current.height + dy);
    setSize({ width: nextWidth, height: nextHeight });
  };

  const handleMouseUp = () => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Cleanup on unmount or when maximized toggles
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      isResizingRef.current = false;
    };
  }, []);

  const handleResizeStart = (e) => {
    if (isMaximized) return;
    e.preventDefault();
    isResizingRef.current = true;
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = { width: size.width, height: size.height };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleMinimize = () => {
    if (onMinimize) onMinimize();
  };

  const handleMaximize = () => {
    if (onMaximize) onMaximize();
  };

  // Prevent buttons from stealing focus on mouse click (keeps UI from showing dotted rectangles)
  const preventFocus = (e) => {
    e.preventDefault();
  };

  const windowContent = (
    <div 
      className={`window z-50 ${
        isMaximized ? 'fixed top-0 left-0' : 'absolute'
      }`}
      style={{
        width: isMaximized ? '100vw' : `${size.width}px`,
        height: isMaximized ? 'calc(100vh - 30px)' : `${size.height}px`,
        minWidth: isMaximized ? undefined : `${MIN_WIDTH}px`,
        minHeight: isMaximized ? undefined : `${MIN_HEIGHT}px`,
        maxWidth: '100vw',
        maxHeight: 'calc(100vh - 30px)'
      }}
    >
      {/* Title Bar using XP.css classes */}
      <div className="title-bar">
        <div className="title-bar-text">
          {icon && (
            <img 
              src={icon} 
              alt={title}
              className="inline w-4 h-4 mr-2"
              style={{ imageRendering: 'pixelated' }}
            />
          )}
          {title}
        </div>
        <div className="title-bar-controls">
          <button 
            aria-label="Minimize"
            onClick={handleMinimize}
          ></button>
          <button 
            aria-label={isMaximized ? "Restore" : "Maximize"}
            onClick={handleMaximize}
          ></button>
          <button 
            aria-label="Close"
            onClick={handleClose}
          ></button>
        </div>
      </div>

      {/* Menu Bar */}
      <div className="bg-[#f0f0f0] border-b border-[#d4d0c8] px-2 py-1 text-[11px]" style={{ fontFamily: 'Tahoma, sans-serif' }}>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">File</span>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">Edit</span>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">View</span>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">Favorites</span>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">Tools</span>
        <span className="px-2 py-1 cursor-pointer hover:bg-[#316ac5] hover:text-white rounded-sm">Help</span>
      </div>

      {/* Toolbar */}
      <div className="toolbar bg-[#ece9d8] border-b border-[#aca899] px-2 py-1 flex items-center gap-1 text-[11px]" style={{ fontFamily: 'Tahoma, sans-serif' }}>
        <div className="flex items-center gap-1">
          <button className="flex items-center px-2 py-1 bg-transparent border-none">
            <img src="/back.png" alt="Back" className="w-5 h-5 mr-1" />
            Back
          </button>
          <button className="tool-icon inline-flex items-center justify-center p-0 w-[16px] h-[16px] bg-transparent border-none">
            <img src="/front.png" alt="Forward" className="w-3 h-3 object-contain" />
          </button>
          <button className="tool-icon inline-flex items-center justify-center p-0 w-[16px] h-[16px] bg-transparent border-none">
            <img src="/up-folder.png" alt="Up" className="w-3 h-3 object-contain" />
          </button>
        </div>
        
        <div className="h-4 w-px bg-[#aca899] mx-1" />
        
        <button className="flex items-center px-2 py-1 bg-transparent border-none">
          <img src="/search.ico" alt="Search" className="w-5 h-5 mr-1" />
          Search
        </button>
        
        <button className="flex items-center px-2 py-1 bg-transparent border-none">
          <img src="/folder.ico" alt="Folders" className="w-5 h-5 mr-1" />
          Folders
        </button>
        
        <div className="h-4 w-px bg-[#aca899] mx-1" />
        
        <button className="flex items-center px-2 py-1 bg-transparent border-none">
          <img src="/broken-views.png" alt="Views" className="w-5 h-5" />
        </button>
      </div>

      {/* Address Bar */}
      <div className="bg-[#ece9d8] border-b border-[#aca899] px-2 h-[22px] flex items-center gap-0 text-[11px]" style={{ fontFamily: 'Tahoma, sans-serif' }}>
        <span className="text-[#666]">Address</span>
        <div className="flex items-center flex-1 min-w-0">
          {/* Address field with blue XP-like border */}
          <div className="flex items-center bg-white h-full pl-2 pr-0 border border-[#7aa6ff] shadow-[inset_0_0_0_1px_#ffffff,0_0_0_1px_#c6d7ff] rounded-l-[2px] flex-1 min-w-0">
            <img src="/my-computer.ico" alt="Folder" className="w-4 h-4 mr-2" />
            <span className="truncate">{title}</span>
          </div>
          {/* Integrated dropdown icon-only (no extra rectangle), height matched to input */}
          <span
            role="button"
            tabIndex={0}
            aria-label="Open address options"
            onMouseDown={preventFocus}
            className="inline-flex items-center justify-center w-[20px] h-full -ml-[1px] m-0 p-0 bg-transparent select-none align-middle leading-none shrink-0"
          >
            <img src="/dropdown.png" alt="Dropdown" className="block w-[20px] h-[20px]" />
          </span>
        </div>
        {/* Go icon-only square (no surrounding rectangle), height matched to input */}
        <span
          role="button"
          tabIndex={0}
          aria-label="Go"
          onMouseDown={preventFocus}
          className="inline-flex items-center justify-center w-[20px] h-full p-0 ml-[2px] m-0 bg-transparent select-none align-middle leading-none shrink-0"
        >
          <img src="/go.ico" alt="Go" className="block w-[20px] h-[20px]" />
        </span>
        <span className="text-[#222] ml-1 leading-[22px]">Go</span>
      </div>

      {/* Window Content */}
      <div className="window-body" style={{ 
        padding: 0,
        height: 'calc(100% - 110px)',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {children}
      </div>

      {/* Resize handle (bottom-right) */}
      {!isMaximized && (
        <div
          onMouseDown={handleResizeStart}
          aria-label="Resize window"
          role="separator"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
            }
          }}
          className="absolute right-0 bottom-0 w-5 h-5 cursor-se-resize"
          style={{
            // simple visual grip using borders
            borderRight: '2px solid #9da3ae',
            borderBottom: '2px solid #9da3ae'
          }}
        />
      )}
    </div>
  );

  if (isMaximized) {
    return windowContent;
  }

  return (
    <Draggable
      handle=".title-bar"
      defaultPosition={{ x, y }}
    >
      {windowContent}
    </Draggable>
  );
};

export default Window;
